"use client";

import React from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Clock, Droplet, Flame, PhoneCall, ShieldCheck, Wrench, Zap } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const emergencyServices = [{
  icon: Droplet,
  title: "Burst Pipes & Floods",
  time: "Immediate response"
}, {
  icon: Flame,
  title: "Water Heater Failure",
  time: "Same-day repair"
}, {
  icon: AlertCircle,
  title: "Severe Drain Clogs",
  time: "24/7 dispatch"
}, {
  icon: Wrench,
  title: "Sewer Line Backups",
  time: "Emergency crew ready"
}];
const guarantees = ["24/7 Live dispatchers", "Licensed & insured plumbers", "Upfront pricing before work starts", "Arriving in 45 minutes or less across Jonesboro"];
export default function EmergencyHero() {
  return <section id="emergencyplumbinghero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Background Decorative Accent Gradients */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[800px] rounded-full bg-secondary/50 blur-3xl" />
        <div className="absolute top-1/3 -right-24 h-72 w-72 rounded-full bg-accent/15 blur-2xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Urgent Messaging & Direct CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            
            {/* Urgent Status Badge */}
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="px-3 py-1 font-semibold flex items-center gap-1.5 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive-foreground opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive-foreground" />
                </span>
                {"24/7 EMERGENCY ON-CALL DISPATCH"}
              </Badge>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="size-3.5 text-accent" />
                {"Jonesboro & Clayton County"}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-default leading-tight">
              {"Plumbing Emergency in Jonesboro?"} <br />
              <span className="text-primary underline decoration-accent decoration-4 underline-offset-4">
                {"Help Is on the Way in Minutes."}
              </span>
            </h1>

            {/* Subheading / Description */}
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground font-default max-w-2xl">
              {"Flooding, broken pipes, backed-up sewers, or no hot water? Jonesboro Emergency Plumber delivers immediate on-site relief with clear, upfront pricing and zero hidden fees."}
            </p>

            {/* Primary Action Buttons */}
            <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-destructive hover:bg-destructive/90 text-destructive-foreground text-base sm:text-lg font-bold py-6 px-8 shadow-md transition-[color,background-color,border-color,box-shadow]">
                  <PhoneCall className="size-5 sm:size-6" />
                  {"Call (470) 847-9076"}
                </Button>
              </a>

              <Link to="/services/" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-secondary text-base font-semibold py-6 px-6 transition-[color,background-color,border-color,box-shadow]">
                  <Wrench className="size-4" />
                  {"View Emergency Services"}
                </Button>
              </Link>
            </div>

            {/* Quick Guarantees List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-4 w-full">
              {guarantees.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span>{item}</span>
                </div>)}
            </div>

          </div>

          {/* Right Column: Hero Visual & Rapid Dispatch Card */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            
            {/* Visual Media Card */}
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl bg-card">
              <div className="relative h-64 sm:h-80 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumber_van_jonesboro.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumber_van_jonesboro.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumber_van_jonesboro.webp-wvc-sizes" alt="Jonesboro emergency plumbing service van ready for rapid dispatch" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
                
                <div className="absolute bottom-4 left-4 right-4 text-primary-foreground">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="size-5 text-accent" />
                    <span className="font-bold text-sm tracking-wide uppercase text-accent-foreground bg-accent px-2 py-0.5 rounded-sm">
                      {"Master Certified Technicians"}
                    </span>
                  </div>
                  <p className="text-sm text-primary-foreground font-medium">
                    {"Fully stocked trucks arriving ready to repair on the first trip."}
                  </p>
                </div>
              </div>

              {/* Rapid Service Highlights Inside Card */}
              <CardContent className="p-5 bg-card">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 font-default">
                  {"Immediate Response Issues We Solve Right Now"}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {emergencyServices.map((service, i) => {
                  const IconComponent = service.icon;
                  return <div key={i} data-index={i} className="flex flex-col p-3 rounded-lg bg-secondary/60 border border-border/70">
                        <div className="flex items-center gap-2 text-primary font-semibold text-xs sm:text-sm mb-1">
                          <IconComponent className="size-4 text-accent shrink-0" />
                          <span className="truncate">{service.title}</span>
                        </div>
                        <span className="text-[11px] sm:text-xs text-muted-foreground">
                          {service.time}
                        </span>
                      </div>;
                })}
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-foreground font-medium">
                    <Zap className="size-4 text-accent shrink-0" />
                    <span>{"Average Arrival Time:"}</span>
                    <strong className="text-primary">{"25-45 mins"}</strong>
                  </div>
                  <Link to="/contact/">
                    <Button variant="ghost" size="sm" className="text-xs text-primary font-bold hover:bg-muted p-1 sm:px-2">
                      {"Request Call Back"}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </div>

          </div>

        </div>
      </div>
    </section>;
}